import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { reportEvent } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';

export interface TemplateBrief {
  scene: string;
  scene_name?: string;
}

export interface TemplateDetailState {
  scene: string;
  title?: string;
  index?: number;
}

interface UseTemplateDetailParams<T extends TemplateBrief> {
  templates: T[];
  hasMore: boolean;
  isLoading?: boolean;
  loadMore: () => void;
  reportPosition: string;
  queryParamKey?: string; // default: 'detail'
}

export function useTemplateDetail<T extends TemplateBrief>({
  templates,
  hasMore,
  isLoading = false,
  loadMore,
  reportPosition,
  queryParamKey = 'detail',
}: UseTemplateDetailParams<T>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [detail, setDetail] = useState<TemplateDetailState | null>(null);

  const setUrlParam = (scene: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(queryParamKey, scene);
    router.replace(`${pathname}?${params.toString()}`);
  };

  const removeUrlParam = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(queryParamKey);
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : `${pathname}`);
  };

  const openDetail = (scene: string, title: string, index: number) => {
    setDetail({ scene, title, index });
    setUrlParam(scene);
    reportEvent('templates_detail_exposure', {
      templates_name: scene,
      from_page: reportPosition,
    });
  };

  const closeDetail = () => {
    setDetail(null);
    removeUrlParam();
  };

  const onPrev = () => {
    if (detail?.index === undefined) return;
    const prevIndex = detail.index - 1;
    if (prevIndex >= 0) {
      const t = templates[prevIndex];
      openDetail(t.scene, t.scene_name || '', prevIndex);
    }
  };

  const onNext = () => {
    if (detail?.index === undefined) return;
    const nextIndex = detail.index + 1;
    if (nextIndex < templates.length) {
      const t = templates[nextIndex];
      openDetail(t.scene, t.scene_name || '', nextIndex);
    }
  };

  const hasPrev = (detail?.index ?? 0) > 0;
  const hasNext = detail?.index !== undefined && detail.index < templates.length - 1;

  // Sync drawer open/close with query param
  useEffect(() => {
    const sceneParam = searchParams.get(queryParamKey);
    if (sceneParam) {
      if (detail?.scene === sceneParam) return;
      const idx = templates.findIndex((t) => t.scene === sceneParam);
      if (idx >= 0) {
        const title = templates[idx]?.scene_name || '';
        setDetail({ scene: sceneParam, title, index: idx });
      } else if (hasMore && !isLoading) {
        loadMore();
      }
    } else if (detail?.scene) {
      setDetail(null);
    }
    // We intentionally depend on templates and search params to respond to changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, templates]);

  return {
    detail,
    openDetail,
    closeDetail,
    onPrev,
    onNext,
    hasPrev,
    hasNext,
  } as const;
}
